import apiClient from './apiClient';
import type { ApiResponse } from '../types/api';

export interface MedicalReferral {
    id: number;
    consultation_id: number;
    referring_doctor_id: number;
    referred_specialty_id: number;
    referred_doctor_id?: number;
    reason: string;
    clinical_summary?: string;
    observations?: string;
    priority: 'routine' | 'urgent' | 'emergency';
    status: 'pending' | 'accepted' | 'completed' | 'cancelled';
    referring_doctor?: { id: number; first_name: string; last_name: string };
    referred_specialty?: { id: number; name: string };
    referred_doctor?: { id: number; first_name: string; last_name: string };
    created_at?: string;
    /**
     * Signing lifecycle, separate from the workflow `status` above (R7).
     * `locked_with_consultation` referrals follow their consultation and keep
     * `signing_status = 'draft'` forever; standalone ones are signed on their own.
     */
    locked_with_consultation?: boolean;
    signing_status?: 'draft' | 'signed';
    signed_by?: number | null;
    signed_at?: string | null;
    signature_method?: string | null;
    lock_version?: number;
    voided_at?: string | null;
    void_reason?: string | null;
    replaces_referral_id?: number | null;
}

export interface CreateMedicalReferralRequest {
    consultation_id: number;
    referring_doctor_id: number;
    referred_specialty_id: number;
    referred_doctor_id?: number;
    reason: string;
    clinical_summary?: string;
    observations?: string;
    priority?: string;
}

/** `void_reason` is why the original is wrong; `reason` is the new referral's own clinical reason. */
export interface ReissueMedicalReferralRequest {
    void_reason: string;
    referred_specialty_id: number;
    referred_doctor_id?: number;
    reason: string;
    clinical_summary?: string;
    observations?: string;
    priority?: string;
}

interface PaginatedData<T> {
    data: T[];
    pagination?: { current_page: number; last_page: number; total: number };
}

export const medicalReferralService = {
    async getByConsultation(consultationId: number): Promise<MedicalReferral[]> {
        const response = await apiClient.get<PaginatedData<MedicalReferral>>(
            `/medical-referrals?consultation_id=${consultationId}&per_page=50`
        );
        return response.data.data;
    },

    async create(data: CreateMedicalReferralRequest): Promise<MedicalReferral> {
        const response = await apiClient.post<ApiResponse<MedicalReferral>>(
            '/medical-referrals',
            data
        );
        return response.data.data;
    },

    async update(id: number, data: Partial<CreateMedicalReferralRequest & { status: string }>): Promise<MedicalReferral> {
        const response = await apiClient.put<ApiResponse<MedicalReferral>>(
            `/medical-referrals/${id}`,
            data
        );
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/medical-referrals/${id}`);
    },

    /** Sign a standalone draft referral (R7); 409 when `lockVersion` is stale. */
    async sign(id: number, lockVersion: number): Promise<MedicalReferral> {
        const response = await apiClient.post<ApiResponse<MedicalReferral>>(
            `/medical-referrals/${id}/sign`,
            { lock_version: lockVersion }
        );
        return response.data.data;
    },

    /** Void a signed referral and issue its signed replacement in one call (R8). */
    async reissue(id: number, data: ReissueMedicalReferralRequest): Promise<MedicalReferral> {
        const response = await apiClient.post<ApiResponse<MedicalReferral>>(
            `/medical-referrals/${id}/reissue`,
            data
        );
        return response.data.data;
    },
};

export default medicalReferralService;
